import React from "react"
import { Box } from "@mui/material"
import { ArrowForward } from "@mui/icons-material"
import RocketIcon from "src/assets/icons/rocket.svg"
import { POOLS_MAP, PoolName, PoolTypes } from "src/constants"
import { formatBNToString, formatApr, formatBoostedApr } from "src/utils"
import usePoolData, {
  calculateShareFactorByMultiplier,
} from "src/hooks/usePoolData"

import Text from "../Text"
import QuestionHelper from "../QuestionHelper"

export const PoolItem: React.FC<{
  poolName: PoolName
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
}> = ({ poolName, selected = false, disabled = false, onClick }) => {
  const pool = POOLS_MAP[poolName]
  const { type: poolType, lpToken } = pool
  const formattedDecimals = poolType === PoolTypes.USD ? 2 : 4
  const [poolData, userShareData, boostData] = usePoolData(poolName)
  const boostFactor = boostData?.potentialBoostFactor
    ? parseFloat(formatBNToString(boostData.potentialBoostFactor, 18))
    : 1
  const nonStaked = !userShareData?.stakedAmount?.gt(0)
  disabled = disabled || nonStaked

  return (
    <Box
      sx={{
        gap: "12px",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: "10px",
        background: selected ? "var(--outline-gradient)" : "transparent",
      }}
      onClick={!disabled && onClick ? onClick : () => undefined}
    >
      <Box
        position="relative"
        display="flex"
        alignItems="center"
        padding="16px"
        margin="1px"
        sx={{
          borderRadius: "10px",
          backgroundColor: "#000000",
        }}
      >
        <Box
          height="104px"
          display="flex"
          flex="1"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <img src={lpToken.icon} height={24} />
            <Text>{poolName}</Text>
          </Box>
          <Box>
            <Text mb="4px" size="micro">
              Your staked liquidity
            </Text>
            <Text>
              {userShareData?.stakedAmount &&
                formatBNToString(
                  userShareData?.stakedAmount,
                  lpToken.decimals,
                  formattedDecimals,
                )}
            </Text>
          </Box>
        </Box>
        <Box
          height="104px"
          display="flex"
          flex="1"
          flexDirection="column"
          justifyContent="space-between"
          borderLeft="1px solid #505050"
          pl="16px"
        >
          <Box>
            <Text mb="4px" size="micro">
              Current APR
            </Text>
            <Text>{formatApr(poolData?.apr)}</Text>
          </Box>
          <Box>
            <Box alignItems="center" display="flex" gap={1} mb="4px">
              <Text size="micro">Boosted APR estimate</Text>
              <QuestionHelper text="The estimated APR after applying pool boost. Please note that it does not represent the guaranteed return from the pool." />
            </Box>
            <Text>
              {nonStaked
                ? "Need to stake LP first"
                : formatBoostedApr(
                    poolData?.apr,
                    poolData?.ferroApr,
                    boostFactor,
                  )}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const BoostedPool: React.FC<{
  poolName: PoolName
}> = ({ poolName }) => {
  const pool = POOLS_MAP[poolName]
  const { lpToken } = pool
  const [poolData, , boostData] = usePoolData(poolName)
  const boostFactor = boostData?.boostFactor
    ? parseFloat(formatBNToString(boostData.boostFactor, 18))
    : 1
  const maxBoostFactor = boostData?.maxBoostFactor
    ? parseFloat(formatBNToString(boostData.maxBoostFactor, 18))
    : 1

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box display="flex" alignItems="center" gap={1}>
        <img src={lpToken.icon} height={21} />
        <Text size="small">{poolName}</Text>
      </Box>
      <Box alignItems="center" display="flex" gap={0.5}>
        <img src={RocketIcon} height={12} />
        <Text
          size="small"
          color="#00A68C"
          title={`Max APR: ${formatBoostedApr(
            poolData?.apr,
            poolData?.ferroApr,
            maxBoostFactor,
          )}`}
        >
          {formatBoostedApr(poolData?.apr, poolData?.ferroApr, boostFactor)}
        </Text>
      </Box>
    </Box>
  )
}

export const BoostedItem: React.FC<{
  poolName: PoolName
  multiplier?: number
}> = ({ poolName, multiplier }) => {
  const pool = POOLS_MAP[poolName]
  const { lpToken } = pool
  const [poolData, userShareData, boostData] = usePoolData(poolName)
  const boostFactor = boostData?.boostFactor
    ? parseFloat(formatBNToString(boostData.boostFactor, 18))
    : 1
  const { boostFactor: estimateBoostFactorBN } =
    calculateShareFactorByMultiplier(
      multiplier,
      userShareData?.stakedAmount,
      boostData?.totalAdjustLpAmount,
      boostData?.adjustLpAmount,
      userShareData?.stakedShare,
      boostData?.baseAllocation,
      boostData?.boostAllocation,
    )
  const estimateBoostFactor = parseFloat(
    formatBNToString(estimateBoostFactorBN, 18),
  )
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box display="flex" alignItems="center" gap={1}>
        <img src={lpToken.icon} height={21} />
        <Text size="small">{poolName}</Text>
      </Box>
      <Box alignItems="center" display="flex" gap={0.5}>
        <Text size="small">
          {formatBoostedApr(poolData?.apr, poolData?.ferroApr, boostFactor)}
        </Text>
        <ArrowForward fontSize="small" />
        <Text
          size="small"
          color={estimateBoostFactor < boostFactor ? "#d9475a" : "#00A68C"}
        >
          {multiplier &&
            formatBoostedApr(
              poolData?.apr,
              poolData?.ferroApr,
              estimateBoostFactor,
            )}
        </Text>
      </Box>
    </Box>
  )
}
